/**
 * ProjectAskPanel — natural-language cross-meeting search for a project.
 *
 * The user types a question; the server runs FTS to shortlist finalized
 * meetings, feeds them to Claude Sonnet, and returns a structured
 * synthesis (summary / keyPoints / openQuestions / timeline). Each
 * citation is a clickable date badge that links back to the source
 * meeting.
 *
 * The original keyword search (ts_headline snippet list) is still one
 * click away under a "Show raw matches" toggle — useful when the
 * synthesis misses the mark or the user wants to jump directly to a
 * specific meeting.
 *
 * Mockup v4 visual chrome:
 *   - Outer panel: cloud surface + paper-grain texture, charcoal/10
 *     border, rounded-[20px], padding 24/28px (px-7 py-6)
 *   - Header: pine 11px tracked uppercase eyebrow + 13px helper +
 *     "Hide raw matches" toggle pill (bone surface, hover pine)
 *   - Form: full-rounded pill input on bone bg + pine pill submit
 *     with Sparkles icon
 *   - AnswerBlock: bone surface card with FileSearch eyebrow +
 *     duration/cost meta, summary 14px, then 4 sections
 *     (Key points / Open questions / Timeline / Sources read)
 *   - Citations: small pine pills (pine/40 border, pine/5 bg)
 *   - Raw matches: top-divided ProjectArchiveSearch embed
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Sparkles, FileSearch } from 'lucide-react';
import { api, ApiError } from '../lib/apiClient.ts';
import { NO_AUTOFILL } from '../lib/noAutofill.ts';
import { ProjectArchiveSearch } from './ProjectArchiveSearch.tsx';

interface SourceMeeting {
  id: string;
  title: string;
  meetingDate: string;
  rank: number;
}

interface AskAnswer {
  summary: string;
  keyPoints: Array<{ text: string; meetingIds: string[] }>;
  openQuestions: Array<{ text: string; meetingIds: string[] }>;
  timeline: Array<{
    meetingId: string;
    meetingDate: string;
    contribution: string;
  }>;
}

interface AskResponse {
  answer: AskAnswer | null;
  sourceMeetings: SourceMeeting[];
  query: string;
  reason?: 'no_fts_matches';
  usage?: {
    costUsd: number;
    inputTokens: number;
    outputTokens: number;
    durationMs: number;
  };
}

interface Props {
  projectId: string;
  projectName: string;
}

export function ProjectAskPanel({ projectId, projectName }: Props) {
  const [query, setQuery] = useState('');
  const [showRawMatches, setShowRawMatches] = useState(false);

  const ask = useMutation({
    mutationFn: (q: string) =>
      api.post<AskResponse>('/meetings/search/ask', {
        projectId,
        query: q,
      }),
  });

  // Lookup from meeting id → source meeting object so citation badges
  // can render the date + title without a separate fetch.
  const sourceById = useMemo(() => {
    const map = new Map<string, SourceMeeting>();
    if (ask.data?.sourceMeetings) {
      for (const s of ask.data.sourceMeetings) map.set(s.id, s);
    }
    return map;
  }, [ask.data?.sourceMeetings]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length < 3) return;
    ask.mutate(trimmed);
  };

  return (
    // Outer panel — cloud surface + paper-grain texture so the
    // search affordance reads as part of the project header section,
    // not a separate utility surface. Padding scaled to mockup
    // (24/28px = py-6 px-7).
    <section className="paper-grain relative overflow-hidden rounded-[20px] border border-charcoal/10 bg-cloud px-7 py-6 shadow-sm">
      <div className="relative">
        <div className="mb-4 flex items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-pine">
              Ask this project
            </h2>
            <p className="mt-1.5 max-w-[60ch] text-[13px] leading-relaxed text-charcoal/75">
              Ask a question across all finalized meetings in{' '}
              <span className="font-semibold text-charcoal">{projectName}</span>.
              Claude weaves the answer from the meetings that mention your
              topic.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowRawMatches((v) => !v)}
            className="shrink-0 whitespace-nowrap rounded-full border border-charcoal/15 bg-bone px-3.5 py-1.5 text-[11px] font-medium text-charcoal transition-colors hover:border-pine hover:text-pine"
            title="Switch to the keyword-match snippet list."
          >
            {showRawMatches ? 'Hide raw matches' : 'Show raw matches'}
          </button>
        </div>

        {/* Ask form — pill input on bone bg + pine pill submit */}
        <form
          onSubmit={onSubmit}
          className="grid grid-cols-[1fr_auto] items-stretch gap-2"
        >
          <input
            {...NO_AUTOFILL}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. What did we decide about the central plaza materials?"
            className="w-full rounded-full border border-charcoal/10 bg-bone px-5 py-2.5 text-[15px] font-light text-charcoal transition-all placeholder:text-charcoal/45 focus:border-pine focus:bg-paper-light focus:outline-none focus:ring-[3px] focus:ring-pine/15"
            autoComplete="off"
          />
          <button
            type="submit"
            disabled={ask.isPending || query.trim().length < 3}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-pine px-5 py-2.5 text-[13px] font-medium text-cloud transition-all hover:-translate-y-px hover:bg-pine-deep disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Sparkles size={14} />
            {ask.isPending ? 'Searching…' : 'Ask'}
          </button>
        </form>

        {/* Loading */}
        {ask.isPending && (
          <div className="mt-5 rounded-2xl border border-charcoal/10 bg-bone px-5 py-4 text-sm text-charcoal/70">
            <div className="flex items-center gap-2 text-pine">
              <Sparkles size={14} className="animate-pulse" />
              <span className="text-[13px] font-semibold">
                Reading across finalized meetings…
              </span>
            </div>
            <div className="mt-2 text-[12px] text-charcoal/60">
              Claude is looking for every mention and weaving the answer.
              Usually 10–25 seconds.
            </div>
          </div>
        )}

        {/* Error */}
        {ask.isError && (
          <div className="mt-5 rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 px-5 py-3 text-sm text-terra-cotta">
            {ask.error instanceof ApiError ? ask.error.message : 'Ask failed'}
          </div>
        )}

        {/* No matches */}
        {ask.data && ask.data.answer === null && (
          <div className="mt-5 rounded-2xl border border-dashed border-charcoal/25 bg-bone/60 px-5 py-6 text-center text-sm text-charcoal/65">
            No finalized meetings in{' '}
            <span className="font-semibold text-charcoal">{projectName}</span>{' '}
            mention &ldquo;{ask.data.query}&rdquo;. Try different words, or
            check that the meetings you&rsquo;re thinking of are finalized.
          </div>
        )}

        {/* Answer */}
        {ask.data && ask.data.answer && (
          <AnswerBlock
            answer={ask.data.answer}
            sourceById={sourceById}
            sourceMeetings={ask.data.sourceMeetings}
            usage={ask.data.usage}
          />
        )}

        {/* Raw matches (the original keyword search) */}
        {showRawMatches && (
          <div className="mt-6 border-t border-charcoal/10 pt-5">
            <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-pine">
              Raw keyword matches
            </h3>
            <ProjectArchiveSearch
              projectId={projectId}
              projectName={projectName}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function AnswerBlock({
  answer,
  sourceById,
  sourceMeetings,
  usage,
}: {
  answer: AskAnswer;
  sourceById: Map<string, { id: string; title: string; meetingDate: string }>;
  sourceMeetings: SourceMeeting[];
  usage?: { costUsd: number; inputTokens: number; outputTokens: number; durationMs: number };
}) {
  return (
    // Bone surface card — sits inside the cloud ask-panel so the
    // answer reads as a "card within a card" composition.
    <div className="mt-5 rounded-2xl border border-charcoal/10 bg-bone px-6 py-5">
      {/* Header — FileSearch eyebrow on left, duration/cost meta on right */}
      <div className="flex items-baseline justify-between gap-3 border-b border-charcoal/10 pb-3">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-pine">
          <FileSearch size={12} />
          Synthesis across {sourceMeetings.length} meeting
          {sourceMeetings.length === 1 ? '' : 's'}
        </div>
        {usage && (
          <div className="text-[11px] tabular-nums text-charcoal/55">
            {(usage.durationMs / 1000).toFixed(1)}s · $
            {usage.costUsd.toFixed(3)}
          </div>
        )}
      </div>

      {/* Summary narrative */}
      <p className="mb-4 mt-4 whitespace-pre-wrap text-[14px] leading-[1.6] text-charcoal">
        {answer.summary}
      </p>

      {/* Key points */}
      {answer.keyPoints.length > 0 && (
        <Section title="Key points">
          <ul className="m-0 list-none p-0">
            {answer.keyPoints.map((k, i) => (
              <li
                key={i}
                className="grid grid-cols-[auto_1fr] items-baseline gap-2.5 py-1.5"
              >
                <span className="mt-2 h-[5px] w-[5px] shrink-0 rounded-full bg-pine" />
                <div>
                  <div className="text-[13px] leading-[1.55] text-charcoal">
                    {k.text}
                  </div>
                  <CitationRow ids={k.meetingIds} sourceById={sourceById} />
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Open questions */}
      {answer.openQuestions.length > 0 && (
        <Section title="Open questions">
          <ul className="m-0 list-none p-0">
            {answer.openQuestions.map((q, i) => (
              <li
                key={i}
                className="grid grid-cols-[auto_1fr] items-baseline gap-2.5 py-1.5"
              >
                <span className="text-[14px] font-bold leading-[1.4] text-terra-cotta">
                  ?
                </span>
                <div>
                  <div className="text-[13px] leading-[1.55] text-charcoal">
                    {q.text}
                  </div>
                  <CitationRow ids={q.meetingIds} sourceById={sourceById} />
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Timeline — pine pill date + contribution text */}
      {answer.timeline.length > 0 && (
        <Section title="Timeline">
          <ol className="m-0 flex list-none flex-col gap-2 p-0">
            {answer.timeline.map((t, i) => (
              <li
                key={i}
                className="grid grid-cols-[auto_1fr] items-baseline gap-2.5 py-1"
              >
                <Link
                  to={`/meetings/${t.meetingId}/edit`}
                  className="inline-block whitespace-nowrap rounded-full border border-pine/40 bg-pine/5 px-2.5 py-0.5 text-[11px] font-medium tabular-nums text-pine no-underline transition-colors hover:bg-pine/10"
                >
                  {t.meetingDate}
                </Link>
                <span className="text-[13px] leading-[1.55] text-charcoal">
                  {t.contribution}
                </span>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* Sources read — cream pills (the FTS shortlist Claude saw).
          Useful audit trail: "why didn't Claude mention meeting X?"
          answered by either "not in the shortlist" or "nothing in X
          was relevant to the question". */}
      <Section title="Sources read">
        <div className="flex flex-wrap gap-1.5">
          {sourceMeetings.map((s) => (
            <Link
              key={s.id}
              to={`/meetings/${s.id}/edit`}
              className="inline-block rounded-full border border-charcoal/20 bg-cream px-3 py-1 text-[11px] tabular-nums text-charcoal no-underline transition-colors hover:border-pine hover:text-pine"
              title={s.title}
            >
              {s.meetingDate}
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4">
      <h4 className="m-0 mb-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-pine">
        {title}
      </h4>
      {children}
    </div>
  );
}

function CitationRow({
  ids,
  sourceById,
}: {
  ids: string[];
  sourceById: Map<string, { id: string; title: string; meetingDate: string }>;
}) {
  if (ids.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {ids.map((id) => {
        const src = sourceById.get(id);
        // Defensive: Claude *should* only cite ids from the shortlist,
        // but if it hallucinates we fall back to showing the raw id
        // instead of crashing.
        if (!src) {
          return (
            <span
              key={id}
              className="inline-block rounded-full border border-charcoal/15 bg-bone px-2 py-0.5 text-[10px] text-charcoal/50"
              title="Citation id not in shortlist"
            >
              unknown
            </span>
          );
        }
        return (
          <Link
            key={id}
            to={`/meetings/${src.id}/edit`}
            className="inline-block rounded-full border border-pine/40 bg-pine/5 px-2 py-0.5 text-[10px] font-medium tabular-nums text-pine no-underline transition-colors hover:bg-pine/10"
            title={src.title}
          >
            {src.meetingDate}
          </Link>
        );
      })}
    </div>
  );
}
